import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Button,
  HStack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { InternalIntegrationTestingUpdateNotesReadOnlyFormTable } from "features/internal/integration-testing/InternalIntegrationTestingUpdateNotesFormTable";
import {
  useInternalIntegrationTestingFilesUpdate,
  useInternalIntegrationTestingReportComparisonRetrieveSuspense,
  useInternalIntegrationTestingReportFeatureFlagComparisonTableSuspense,
  useInternalIntegrationTestingReportGitCommitComparisonTableSuspense,
  useInternalIntegrationTestingReportRetrieveSuspense,
  useInternalIntegrationTestingReportTableSuspense,
  useInternalIntegrationTestingReportTestComparisonTableSuspense,
} from "gen/endpoints/integration-testing-reports/integration-testing-reports";
import { IntegrationTestCheckTypeEnum } from "gen/model/integrationTestCheckTypeEnum";
import { IntegrationTestStatusEnum } from "gen/model/integrationTestStatusEnum";
import { TestingReportAPIData } from "gen/model/testingReportAPIData";
import { TestingReportComparisonAPIData } from "gen/model/testingReportComparisonAPIData";
import { TFunction } from "i18next";
import { isEmpty } from "lodash-es";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { DocumentDownloadLink } from "shared/components/link/DownloadDocumentLink";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import {
  GetLinkInfoForCellFn,
  RenderCellValueFn,
} from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { CheckIcon } from "shared/icons/CheckIcon";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { XIcon } from "shared/icons/XIcon";
import { rootRoute } from "shared/navigation/Routes";
import { useQueryParam } from "use-query-params";

export type IntegrationTestingFilesUpdateArgs = Parameters<
  ReturnType<typeof useInternalIntegrationTestingFilesUpdate>["mutateAsync"]
>[0];

const DOCUMENT_COLUMN_IDS = [
  "comparison_document",
  "actual_document",
  "expected_document",
];

const TAB_IDS = [
  "commitComparison",
  "testComparison",
  "featureFlagComparison",
] as const;

const TEST_TYPES_TO_RENDER_COMPARISON_TABLE: IntegrationTestCheckTypeEnum[] = [
  IntegrationTestCheckTypeEnum.run_full_suite,
  IntegrationTestCheckTypeEnum.run_validation_checks,
];
type TabId = (typeof TAB_IDS)[number];

const TestingReportStatGroup: React.FC<{
  testingReportData: TestingReportAPIData;
  t: TFunction<["common", "internal"]>;
}> = ({ testingReportData, t }) => (
  <StatGroup>
    <Stat size="xs">
      <StatLabel>{t("common:totalProcessingTime")}</StatLabel>
      <StatNumber>{testingReportData.total_process_time}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>{t("internal:integrationTesting.stats.totalTests")}</StatLabel>
      <StatNumber>{testingReportData.total_tests}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>
        {t("internal:integrationTesting.stats.passedTests")}
      </StatLabel>
      <StatNumber>{testingReportData.passed_tests}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>
        {t("internal:integrationTesting.stats.failedTests")}
      </StatLabel>
      <StatNumber>{testingReportData.failed_tests}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>
        {t("internal:integrationTesting.stats.uniqueFailedTests")}
      </StatLabel>
      <StatNumber>{testingReportData.unique_failed_tests}</StatNumber>
    </Stat>
  </StatGroup>
);

const GitCommitComparisonStatGroup: React.FC<{
  comparisonData: TestingReportComparisonAPIData;
  t: TFunction<["internal"]>;
}> = ({ comparisonData, t }) => (
  <StatGroup justifyContent="flex-start">
    <Stat size="xs">
      <StatLabel>
        {`${t("internal:integrationTesting.stats.commitCount")} ${t("internal:integrationTesting.change")}`}
      </StatLabel>
      <StatNumber>{comparisonData.git_commit_count}</StatNumber>
    </Stat>
  </StatGroup>
);

const FeatureFlagComparisonStatGroup: React.FC<{
  comparisonData: TestingReportComparisonAPIData;
  t: TFunction<["internal"]>;
}> = ({ comparisonData, t }) => (
  <StatGroup justifyContent="flex-start">
    <Stat size="xs">
      <StatLabel>
        {`${t("internal:integrationTesting.stats.enabledFeatureFlags")} ${t("internal:integrationTesting.change")}`}
      </StatLabel>
      <StatNumber>{comparisonData.enabled_feature_flags}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>
        {`${t("internal:integrationTesting.stats.disabledFeatureFlags")} ${t("internal:integrationTesting.change")}`}
      </StatLabel>
      <StatNumber>{comparisonData.disabled_feature_flags}</StatNumber>
    </Stat>
  </StatGroup>
);

const TestComparisonStatGroup: React.FC<{
  comparisonData: TestingReportComparisonAPIData;
  t: TFunction<["internal"]>;
}> = ({ comparisonData, t }) => (
  <StatGroup>
    <Stat size="xs">
      <StatLabel>
        {`${t("internal:integrationTesting.stats.totalTests")} ${t("internal:integrationTesting.change")}`}
      </StatLabel>
      <StatNumber>{comparisonData.total_tests}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>
        {`${t("internal:integrationTesting.stats.passedTests")} ${t("internal:integrationTesting.change")}`}
      </StatLabel>
      <StatNumber>{comparisonData.passed_tests}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>
        {`${t("internal:integrationTesting.stats.failedTests")} ${t("internal:integrationTesting.change")}`}
      </StatLabel>
      <StatNumber>{comparisonData.failed_tests}</StatNumber>
    </Stat>
    <Stat size="xs">
      <StatLabel>
        {`${t("internal:integrationTesting.stats.uniqueFailedTests")} ${t("internal:integrationTesting.change")}`}
      </StatLabel>
      <StatNumber>{comparisonData.unique_failed_tests}</StatNumber>
    </Stat>
  </StatGroup>
);

const ComparisonTableStatGroup: React.FC<{
  selectedTab: TabId;
  comparisonData: TestingReportComparisonAPIData;
  t: TFunction<["internal"]>;
}> = ({ selectedTab, comparisonData, t }) => {
  switch (selectedTab) {
    case "commitComparison":
      return (
        <GitCommitComparisonStatGroup comparisonData={comparisonData} t={t} />
      );
    case "testComparison":
      return <TestComparisonStatGroup comparisonData={comparisonData} t={t} />;
    case "featureFlagComparison":
      return (
        <FeatureFlagComparisonStatGroup comparisonData={comparisonData} t={t} />
      );
    default:
      return null;
  }
};

export const InternalIntegrationTestingReportPage: React.FC = () => {
  const { t } = useTranslation(["internal", "common"]);
  const { integrationTestingReportId } = useRequiredStringParams();
  const navigate = useNavigate();
  const { data: tableData } = useInternalIntegrationTestingReportTableSuspense(
    integrationTestingReportId,
  );

  const { data: testComparisonTableData } =
    useInternalIntegrationTestingReportTestComparisonTableSuspense(
      integrationTestingReportId,
    );

  const { data: featureFlagComparisonTableData } =
    useInternalIntegrationTestingReportFeatureFlagComparisonTableSuspense(
      integrationTestingReportId,
    );

  const { data: gitCommitComparisonTableData } =
    useInternalIntegrationTestingReportGitCommitComparisonTableSuspense(
      integrationTestingReportId,
    );

  const { data: testingReportData } =
    useInternalIntegrationTestingReportRetrieveSuspense(
      integrationTestingReportId,
    );

  const { data: comparisonTestingReportData } =
    useInternalIntegrationTestingReportComparisonRetrieveSuspense(
      integrationTestingReportId,
    );

  const [selectedTab, setSelectedTab] = useQueryParam<TabId | null>("");
  const defaultTab: TabId = gitCommitComparisonTableData.rows.length
    ? "commitComparison"
    : "testComparison";
  const tabIndex = TAB_IDS.indexOf(selectedTab || defaultTab);

  const onChangeTab = useCallback(
    (index: number) => setSelectedTab(TAB_IDS[index]),
    [setSelectedTab],
  );

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const { columnContext, cell } = props;
      if (columnContext.columnId == "passed" && cell.value?.type == "boolean") {
        return cell.value.value ? (
          <CheckIcon color="primary.500" />
        ) : (
          <XIcon color="error.500" />
        );
      } else if (
        columnContext.columnId == "test_number" &&
        cell.value?.type == "integer"
      ) {
        return <div>{cell.value.value}</div>;
      } else if (
        DOCUMENT_COLUMN_IDS.includes(columnContext.columnId) &&
        cell.value?.type == "text"
      ) {
        return cell.value.text ? (
          <DocumentDownloadLink documentId={cell.value.text}>
            <div>{t("common:download")}</div>
          </DocumentDownloadLink>
        ) : undefined;
      }
      return defaultDataTableRenderCellValue(props, fieldDisabled, params);
    },
    [t],
  );

  const getTestComparisonTableLinkInfoForCell =
    useCallback<GetLinkInfoForCellFn>((props) => {
      if (
        props.columnContext.column.id == "ticket_number" &&
        props.type == "data" &&
        props.cell.value?.type == "text"
      ) {
        return {
          type: "generic",
          to: `https://maybern.atlassian.net/browse/${props.cell.value.text}`,
        };
      }
      return undefined;
    }, []);

  const getGitComparisonTableLinkInfoForCell =
    useCallback<GetLinkInfoForCellFn>((props) => {
      if (
        props.columnContext.column.id == "sha" &&
        props.type == "data" &&
        props.cell.value?.type == "text"
      ) {
        return {
          type: "generic",
          to: `https://github.com/Maybern/maybern/commit/${props.cell.value.text}`,
        };
      } else if (
        props.columnContext.column.id == "ticket_number" &&
        props.type == "data" &&
        props.cell.value?.type == "text"
      ) {
        return {
          type: "generic",
          to: `https://maybern.atlassian.net/browse/${props.cell.value.text}`,
        };
      }
      return undefined;
    }, []);

  return (
    <PageContent>
      <PageHeader
        title={t("internal:integrationTestingReport")}
        helperText={t("internal:integrationTestingReportHelpText", {
          createdAt: testingReportData.created_at,
        })}
        actions={
          testingReportData.status != IntegrationTestStatusEnum.success && (
            <Button
              colorScheme="destructive"
              onClick={() =>
                navigate(
                  rootRoute({})
                    .internal({})
                    .integrationTesting({})
                    .reportUpdate({ integrationTestingReportId }).$,
                )
              }
            >
              {t("internal:integrationTesting.updateTestingFiles.title")}
            </Button>
          )
        }
        badge={
          <HStack spacing="8px">
            <Badge colorScheme={testingReportData.check_type_badge.type}>
              {testingReportData.check_type_badge.text}
            </Badge>
            <Badge colorScheme={testingReportData.status_badge.type}>
              {testingReportData.status_badge.text}
            </Badge>
          </HStack>
        }
        backButton={{
          title: t("internal:integrationTesting.title"),
          path: rootRoute({}).internal({}).integrationTesting({}).$,
        }}
      />
      <PageBody>
        {!!testingReportData.error_message && (
          <Alert
            data-ls-enabled={true}
            data-public={true}
            status="error"
            mt="13px"
            mb="13px"
          >
            <AlertIcon>
              <InfoCircleIcon />
            </AlertIcon>
            <AlertDescription>
              {testingReportData.status == IntegrationTestStatusEnum.error
                ? testingReportData.error_message
                : t("internal:integrationTesting.failedTest")}
            </AlertDescription>
          </Alert>
        )}
        {!isEmpty(testingReportData.comparison_customer_names) && (
          <ContentBlockGroup>
            <ContentBlock>
              <ContentBlockHeader>
                {t("internal:integrationTesting.comparingCustomers")}
              </ContentBlockHeader>
              <ContentBlockBody>
                <Descriptions>
                  {testingReportData.comparison_customer_names.map(
                    (customerName, index) => (
                      <Descriptions.Item
                        key={`comparison_customer_${customerName}_${index}`}
                        label={`${t("common:customer")} ${index + 1}`}
                      >
                        {customerName}
                      </Descriptions.Item>
                    ),
                  )}
                </Descriptions>
              </ContentBlockBody>
            </ContentBlock>
          </ContentBlockGroup>
        )}
        {testingReportData.update_metadata && (
          <InternalIntegrationTestingUpdateNotesReadOnlyFormTable
            testingReportUpdateMetadata={testingReportData.update_metadata}
          />
        )}
        <TableBox>
          <TableBoxStats>
            <TestingReportStatGroup
              testingReportData={testingReportData}
              t={t}
            />
          </TableBoxStats>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("internal:integrationTesting.testResults")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={tableData} renderCellValue={renderCellValue} />
          </TableBoxBody>
        </TableBox>
        {!!comparisonTestingReportData.previous_testing_report_id &&
          TEST_TYPES_TO_RENDER_COMPARISON_TABLE.includes(
            testingReportData.check_type,
          ) && (
            <TableBox>
              <TableBoxStats>
                <HStack spacing="32px">
                  <StatGroup>
                    <Stat size="xs">
                      {`${t("common:totalProcessingTime")} ${t("internal:integrationTesting.change")}`}
                      <StatNumber>
                        {comparisonTestingReportData.total_process_time}
                      </StatNumber>
                    </Stat>
                  </StatGroup>
                  <ComparisonTableStatGroup
                    selectedTab={selectedTab || defaultTab}
                    comparisonData={comparisonTestingReportData}
                    t={t}
                  />
                </HStack>
              </TableBoxStats>
              <TableBoxHeader>
                <TableBoxTabs index={tabIndex} onChange={onChangeTab}>
                  <Tab>{t("internal:integrationTesting.gitCommits.title")}</Tab>
                  <Tab>
                    {t("internal:integrationTesting.previousTestComparison")}
                  </Tab>
                  <Tab>
                    {t(
                      "internal:integrationTesting.previousFeatureFlagComparison",
                    )}
                  </Tab>
                </TableBoxTabs>
                <TableBoxBadges>
                  <Badge colorScheme="blue">
                    <Link
                      to={
                        rootRoute({})
                          .internal({})
                          .integrationTesting({})
                          .report({
                            integrationTestingReportId:
                              comparisonTestingReportData.previous_testing_report_id,
                          }).$
                      }
                    >
                      {t("internal:integrationTesting.previousReport")}
                    </Link>
                  </Badge>
                  {comparisonTestingReportData.git_comparison_link && (
                    <Badge colorScheme="warning">
                      <Link
                        to={comparisonTestingReportData.git_comparison_link}
                      >
                        {t("internal:integrationTesting.gitCommitComparison")}
                      </Link>
                    </Badge>
                  )}
                </TableBoxBadges>
                <DataTableBoxActions />
              </TableBoxHeader>
              <TableBoxBody>
                <Tabs variant="table" index={tabIndex} isLazy={true}>
                  <TabPanels>
                    <TabPanel>
                      <DataTable
                        data={gitCommitComparisonTableData}
                        getLinkInfoForCell={
                          getGitComparisonTableLinkInfoForCell
                        }
                        emptyStateProps={{
                          title: t(
                            "internal:integrationTesting.gitCommits.emptyState.title",
                          ),
                          description: t(
                            "internal:integrationTesting.gitCommits.emptyState.description",
                          ),
                        }}
                      />
                    </TabPanel>
                    <TabPanel>
                      <DataTable
                        data={testComparisonTableData}
                        getLinkInfoForCell={
                          getTestComparisonTableLinkInfoForCell
                        }
                        emptyStateProps={{
                          title: t(
                            "internal:integrationTesting.noPreviousTestComparison",
                          ),
                        }}
                      />
                    </TabPanel>
                    <TabPanel>
                      <DataTable
                        data={featureFlagComparisonTableData}
                        emptyStateProps={{
                          title: t(
                            "internal:integrationTesting.noPreviousTestComparison",
                          ),
                        }}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </TableBoxBody>
            </TableBox>
          )}
      </PageBody>
    </PageContent>
  );
};
